if (!customElements.get('product-reviews')) {
  customElements.define(
    'product-reviews',
    class ProductReviews extends HTMLElement {
      autoplayStopped = false;

      connectedCallback() {
        this.viewport = this.querySelector('.product-reviews__viewport');
        this.track = this.querySelector('.product-reviews__track');
        this.group = this.querySelector('.product-reviews__group');
        this.header = this.querySelector('.product-reviews__header');
        if (!this.viewport || !this.track || !this.group) return;

        this.events = new AbortController();
        const options = { signal: this.events.signal };
        this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.focused = false;
        this.touching = false;

        this.viewport.addEventListener('focusin', () => {
          this.focused = true;
          this.stop();
        }, options);
        this.viewport.addEventListener('focusout', () => {
          queueMicrotask(() => {
            this.focused = this.viewport.contains(document.activeElement);
            this.start();
          });
        }, options);
        const touchOptions = { ...options, passive: true };
        this.viewport.addEventListener('touchstart', (event) => {
          const touch = event.touches[0];
          this.swipeStart = event.touches.length === 1 ? { x: touch.clientX, y: touch.clientY } : null;
        }, touchOptions);
        this.viewport.addEventListener('touchmove', (event) => {
          if (!this.swipeStart || this.autoplayStopped || event.touches.length !== 1) return;
          const touch = event.touches[0];
          const distanceX = Math.abs(touch.clientX - this.swipeStart.x);
          const distanceY = Math.abs(touch.clientY - this.swipeStart.y);
          if (distanceX <= 8 || distanceX <= distanceY) return;
          // A horizontal swipe hands control to the reader for the rest of this visit.
          this.autoplayStopped = true;
          clearTimeout(this.resumeTimer);
          this.resumeTimer = null;
          this.stop();
        }, touchOptions);
        const endSwipe = () => { this.swipeStart = null; };
        this.viewport.addEventListener('touchend', endSwipe, touchOptions);
        this.viewport.addEventListener('touchcancel', endSwipe, touchOptions);
        this.viewport.addEventListener('pointerdown', () => {
          this.touching = true;
          this.stop();
        }, options);
        const release = () => {
          if (!this.touching) return;
          this.touching = false;
          this.resumeLater();
        };
        window.addEventListener('pointerup', release, options);
        window.addEventListener('pointercancel', release, options);
        this.viewport.addEventListener('wheel', () => this.resumeLater(), { ...options, passive: true });
        document.addEventListener('visibilitychange', () => {
          document.hidden ? this.stop() : this.start();
        }, options);
        this.motion.addEventListener('change', () => this.measure(), options);

        this.resizeObserver = new ResizeObserver(() => {
          cancelAnimationFrame(this.measureFrame);
          this.measureFrame = requestAnimationFrame(() => this.measure());
        });
        this.resizeObserver.observe(this.viewport);
        this.resizeObserver.observe(this.group);
        this.resizeObserver.observe(this.header);
        this.measure();
      }

      disconnectedCallback() {
        this.events?.abort();
        this.resizeObserver?.disconnect();
        cancelAnimationFrame(this.measureFrame);
        clearTimeout(this.resumeTimer);
        this.resumeTimer = null;
        this.swipeStart = null;
        this.measurement = null;
        this.stop();
        this.copy?.remove();
      }

      measure() {
        const width = this.group.getBoundingClientRect().width;
        const headerStyle = getComputedStyle(this.header);
        const availableWidth = this.header.clientWidth -
          parseFloat(headerStyle.paddingLeft) - parseFloat(headerStyle.paddingRight);
        const viewportWidth = this.clientWidth;
        const reducedMotion = this.motion.matches;
        if (this.measurement && Math.abs(this.measurement.width - width) < 0.5 &&
          Math.abs(this.measurement.availableWidth - availableWidth) < 0.5 &&
          Math.abs(this.measurement.viewportWidth - viewportWidth) < 0.5 &&
          this.measurement.reducedMotion === reducedMotion) return;
        this.measurement = { width, availableWidth, viewportWidth, reducedMotion };

        this.stop();
        this.copy?.remove();
        this.copy = null;
        // Keep a short row aligned with the heading; let a moving row reach the screen edges.
        this.viewport.classList.toggle('page-width', width <= availableWidth + 1);
        this.overflowing = width > this.viewport.clientWidth + 1;

        if (this.overflowing) {
          this.viewport.setAttribute('tabindex', '0');
        } else {
          this.viewport.removeAttribute('tabindex');
          this.viewport.scrollLeft = 0;
        }

        if (!this.overflowing || this.motion.matches) return;
        // One extra group makes the loop continuous; it is skipped by keyboard and screen readers.
        this.copy = this.group.cloneNode(true);
        this.copy.setAttribute('aria-hidden', 'true');
        this.copy.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
        this.track.appendChild(this.copy);
        this.loopWidth = width + parseFloat(getComputedStyle(this.track).columnGap);
        this.start();
      }

      resumeLater() {
        this.stop();
        clearTimeout(this.resumeTimer);
        this.resumeTimer = null;
        if (this.autoplayStopped) return;
        this.resumeTimer = setTimeout(() => {
          this.resumeTimer = null;
          this.start();
        }, 1500);
      }

      start() {
        if (!this.isConnected || this.animation || !this.copy || this.motion.matches || this.autoplayStopped ||
          this.focused || this.touching || this.resumeTimer || document.hidden) return;
        const pixelsPerSecond = 60;
        const offset = this.viewport.scrollLeft % this.loopWidth;
        this.viewport.scrollLeft = 0;
        this.track.classList.add('is-playing');
        // Animate on the compositor instead of changing native scroll position every frame.
        this.animation = this.track.animate([
          { transform: 'translate3d(0, 0, 0)' },
          { transform: `translate3d(-${this.loopWidth}px, 0, 0)` },
        ], {
          duration: this.loopWidth / pixelsPerSecond * 1000,
          iterations: Infinity,
          easing: 'linear',
        });
        this.animation.currentTime = offset / pixelsPerSecond * 1000;
      }

      stop() {
        if (!this.animation) return;
        const progress = this.animation.effect.getComputedTiming().progress ?? 0;
        const offset = this.viewport.scrollLeft + progress * this.loopWidth;
        this.animation.cancel();
        this.animation = null;
        this.track.classList.remove('is-playing');
        // Hand the same visible position to native scrolling before the reader swipes.
        this.viewport.scrollLeft = offset;
      }
    }
  );
}
